import { z } from "zod";

import {
  type CustomQuestionType,
  Demographic,
  type QuestionType,
} from "@tracksuit/frontend/schemas";

export const DEFAULT_ERROR_MESSAGE =
  "We are currently having trouble accessing your latest data.";

export const ACCOUNT_SWITCH_EVENT = "accountBrandSwitch";

export const METRIC_COLORS: {
  [stage in QuestionType | "CATEGORY_PENETRATION"]?: string;
} = {
  UNPROMPTED_AWARENESS: "#EDAE49",
  PROMPTED_AWARENESS: "var(--color-purple-500)",
  CONSIDERATION: "var(--color-orange-500)",
  INVESTIGATION: "var(--color-blue-500)",
  USAGE: "var(--color-teal-500)",
  PREFERENCE: "var(--color-grape-500)",
  CATEGORY_PENETRATION: "var(--color-green-800)",
};

export const COMPETITOR_COLORS = [
  { primary: "var(--color-teal-500)", secondary: "var(--color-teal-300)", dark: "var(--color-teal-700)", text: "var(--color-off-white)" },
  { primary: "var(--color-orange-500)", secondary: "var(--color-orange-300)", dark: "var(--color-orange-700)", text: "var(--color-off-white)" },
  { primary: "var(--color-blue-500)", secondary: "var(--color-blue-300)", dark: "var(--color-blue-700)", text: "var(--color-off-white)" },
  { primary: "var(--color-yellow-500)", secondary: "var(--color-yellow-300)", dark: "var(--color-yellow-700)", text: "var(--color-text)" },
  { primary: "var(--color-grape-500)", secondary: "var(--color-grape-300)", dark: "var(--color-grape-700)", text: "var(--color-off-white)" },
  { primary: "var(--color-coral-500)", secondary: "var(--color-coral-300)", dark: "var(--color-coral-700)", text: "var(--color-off-white)" },
  { primary: "var(--color-peacock-500)", secondary: "var(--color-peacock-300)", dark: "var(--color-peacock-700)", text: "var(--color-off-white)" },
  { primary: "var(--color-fawn-500)", secondary: "var(--color-fawn-300)", dark: "var(--color-fawn-700)", text: "var(--color-off-white)" },
  { primary: "var(--color-charcoal-500)", secondary: "var(--color-charcoal-300)", dark: "var(--color-charcoal-700)", text: "var(--color-off-white)" },
  { primary: "var(--color-sage-500)", secondary: "var(--color-sage-300)", dark: "var(--color-sage-700)", text: "var(--color-off-white)" },
  { primary: "var(--color-cherry-500)", secondary: "var(--color-cherry-300)", dark: "var(--color-cherry-700)", text: "var(--color-off-white)" },
];

export const COMPETITOR_AVG_COLOR = "var(--color-purple-400)";
export const CATEGORY_PENETRATION_COLOR = "var(--color-green-800)";
export const CATEGORY_PENETRATION_SECONDARY_COLOR = "var(--color-green-200)";

export const COMPETITOR_AVERAGE_ID = 1;
export const CATEGORY_PENETRATION_ID = 2;

export const STAGE_EXPLANATIONS: {
  [key in QuestionType]?: {
    label: string;
    verb: string;
    question: string;
    explanation: string;
  };
} = {
  UNPROMPTED_AWARENESS: { label: "unprompted awareness", verb: "are", question: "aware", explanation: "aware of the brand without being prompted" },
  PROMPTED_AWARENESS: { label: "awareness", verb: "are", question: "aware", explanation: "aware of the brand when prompted" },
  CONSIDERATION: { label: "consideration", verb: "would", question: "consider", explanation: "consider using the brand" },
  INVESTIGATION: { label: "investigation", verb: "have", question: "investigated", explanation: "investigated the brand" },
  USAGE: { label: "usage", verb: "have", question: "used", explanation: "used the brand" },
  PREFERENCE: { label: "preference", verb: "would", question: "prefer", explanation: "prefer to use the brand most" },
  QUALIFYING: { label: "qualifying", verb: "are", question: "in the category", explanation: "in the category" },
};

export const DEMOGRAPHIC_EXPLANATIONS: {
  [key in Demographic]?: { verb: string };
} = {
  10000: { verb: "are aged" },
  10011: { verb: "are" },
  10007: { verb: "earn" },
  10008: { verb: "are" },
};

export const DEMOGRAPHIC_TEXT_MODIFIERS: {
  [key in Demographic]?: {
    prefix?: string;
    suffix?: string;
    shouldPluralise?: boolean;
  };
} = {
  10000: { prefix: "those aged" },
  10011: { shouldPluralise: true },
  10017: { shouldPluralise: true },
  10007: { prefix: "those earning" },
};

export const AUTH_ERROR_CODES = {
  CODE_DELIVERY_FAILURE: "CodeDeliveryFailureException",
  NOT_AUTHORIZED: "NotAuthorizedException",
  USERNAME_EXISTS: "UsernameExistsException",
  LIMIT_EXCEEDED: "LimitExceededException",
  MFA_CODE_MISMATCH: "CodeMismatchException",
} as const;

export type AuthErrorCode =
  (typeof AUTH_ERROR_CODES)[keyof typeof AUTH_ERROR_CODES];

export const CONVERSION_IDS = [
  "PROMPTED_AWARENESS-CONSIDERATION",
  "CONSIDERATION-INVESTIGATION",
  "CONSIDERATION-USAGE",
  "CONSIDERATION-PREFERENCE",
  "CONSIDERATION-INVESTIGATION",
  "INVESTIGATION-USAGE",
  "USAGE-PREFERENCE",
] as const;

export const ConversionId = z.enum(CONVERSION_IDS);
export type ConversionId = z.infer<typeof ConversionId>;
